import { createAsyncThunk } from '@reduxjs/toolkit';
import { requestActions } from './requestSlice';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { checkErrorRequest } from '@services/utils/ApiErrors';
import App from '@gral/app';
import logger from '@gral/log';


const moment = require('moment-timezone');
const log = logger('RequestActions');

const getMeta = (meta?: any) => {
  return {
    ...meta,
    organizationId: App.cxStore.getOrganizationId(),
    roleId: meta?.roleId ?? App.cxStore.getRoleId(),
    userDateTime: moment.tz(moment.tz.guess()).format(),
  };
};
const getHeader = (forLogin?: boolean) => {
  const token = App.cxStore.getToken();

  return {
    ...(forLogin && { Accept: 'application/json' }),
    ...(forLogin && { 'Content-Type': 'application/json' }),
    Authorization: `Bearer ${token}`,
  };
};

interface IDataBody {
  login?: string;
  password?: string;
  where?: {
    isMaster?: boolean;
    isAlive?: boolean;
    timestamp?: { $gte: string; $lte: string };
    updatedAt?: { $gte: string; $lte: string };
  };
  filters?: {};
  advancedFilters?: {};
  sortColumn?: string;
  sortOrder?: string;
  findAndCount?: boolean;
  limit?: number;
  offset?: number;
}

interface IDataPost {
  url: string;
  method: string;
  data: any;
  meta: any;
}

export interface IFetchDataInput {
  key?: string; //If there is no key, it is not saved in the request state.
  url: string;
  dataBody?: IDataBody | any;
  meta?: any;
  withoutData?: boolean; // Transfer data in the body
  withoutMeta?: boolean;
  withoutMethod?: boolean;
  withoutCancel?: boolean;
  options?: {
    dataAfterAction?: any;
    afterAction?: (payload: any) => void; // Function to execute after request
  };
}

const cancelTokens: Record<string, CancelTokenSource | null> = {};

const objectToUrlParams = (obj: Record<string, string | number>) => {
  if (Object.keys(obj).length === 0) return '';
  return (
    '?' +
    Object.entries(obj)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')
  );
};

const generateFormDataToUploadFile = (data: IDataPost) => {
  const dataIn = data.data;
  const formData = new FormData();
  let msg = { url: data.url, method: 'upload', data: dataIn.data, meta: data.meta, token: App.cxStore.getToken() };

  for (let key in msg) {
    if (typeof msg[key] === 'object') {
      formData.set(key, JSON.stringify(msg[key]));
    } else {
      formData.set(key, msg[key]);
    }
  }

  formData.set('file', dataIn.file);
  return formData;
};

const bridgeAPIV3Data = (method: string, url: string, data: IDataPost, config) => {
  if (url.startsWith('/api/v3')) {
    //log.info('bridgeAPIV3Data', method, url, data);
    if (method === 'fetch') {
      const isSearch = data.data?.isSearch;
      if (isSearch) {
        delete data.data?.isSearch;
        return axios.post(url + '/search', data.data, config);
      } else {
        return axios.get(url, config);
      }
    }
    if (method === 'patch') {
      return axios.patch(url, data.data, config);
    }
    if (method === 'put') {
      return axios.put(url, data.data, config);
    }
    if (method === 'delete') {
      return axios.delete(url, config);
    }

    return axios.post(url, data.data, config);
  }

  if (method === 'file') {
    //log.info('Post file', method, url, data);
    const formData = generateFormDataToUploadFile(data);
    return axios.post(url, formData, config);
  }
  //log.info('Post V1-V2', method, url, data);
  return axios.post(url, data, config);
};

const createFetchAsyncThunk = (method: string) => {
  return createAsyncThunk(`request/fetchData`, async (input: IFetchDataInput, thunkAPI: any): Promise<any> => {
    const { key, url, dataBody, meta, options } = input;
    const { dispatch, getState, rejectWithValue } = thunkAPI;
    const retryAction = () => dispatch(createFetchAsyncThunk(method)(input));
    const setLoadingState = (value: boolean) => {
      !key && dispatch(requestActions.updateRequestState({ key: 'loading', value }));
    };
    setLoadingState(true);

    const requestId = `fetchData-${key}-${method}-${url}`;
    if (cancelTokens[requestId] && !input?.withoutCancel) {
      cancelTokens[requestId]?.cancel('Cancelled due to new request');
    }
    const source = axios.CancelToken.source();
    cancelTokens[requestId] = source;

    try {
      let dataPost: IDataPost = {
        url,
        ...(!input?.withoutData ? { data: dataBody } : { ...dataBody }),
        ...(!input?.withoutMeta && { meta: getMeta(meta) }),
        ...(!input?.withoutMethod && { method }),
      };

      const response = await bridgeAPIV3Data(method, url, dataPost, {
        //method: 'post',
        headers: getHeader(),
        cancelToken: source.token,
      });
      const data = response.data;
      //log.info('createFetchAsyncThunk', data);
      if (data?.errors || data?.error) {
        const retryResponse = await checkErrorRequest(dispatch, key, data.errors ?? data.error, retryAction, {
          url,
          dataBody,
          meta,
        });

        if (retryResponse?.payload?.data) {
          return { key: retryResponse.payload.key, data: retryResponse.payload.data };
        } else {
          setLoadingState(false);
          return rejectWithValue({ key: key, error: data.errors ?? data.error });
        }
      }

      if (options?.afterAction) {
        const actionData = options.dataAfterAction ? { ...options.dataAfterAction, value: data.data } : { value: data.data };
        const action = options.afterAction(actionData);
        if (action !== undefined) {
          dispatch(action);
        }
      }

      setLoadingState(false);

      return { key, data: url.startsWith('/api/v3') ? data : data.data ?? data };
    } catch (error: AxiosError | any) {
      log.error('createFetchAsyncThunk', error);
      setLoadingState(false);
      if (error.code === 'ERR_CANCELED') return { key: key, data: undefined };

      if (error) {
        const retryResponse = await checkErrorRequest(dispatch, key, error, retryAction, { url, dataBody, meta });

        if (retryResponse?.payload?.data) {
          return { key: retryResponse.payload.key, data: retryResponse.payload.data };
        } else {
          const response = error?.response;
          return rejectWithValue({
            key: key,
            error: response?.data?.error instanceof Object ? response?.data?.error : response?.data,
          });
        }
      }
      //throw error;
    } finally {
      // Clean token after done
      delete cancelTokens[requestId];
    }
  });
};

const loginFetchAsyncThunk = (method: string) => {
  return createAsyncThunk(`request/fetchData`, async (input: IFetchDataInput, thunkAPI): Promise<any> => {
    const { key, url, dataBody } = input;
    const { dispatch, getState } = thunkAPI;
    const retryAction = () => dispatch(createFetchAsyncThunk(method)(input));
    const setLoadingState = (value: boolean) => {
      !key && dispatch(requestActions.updateRequestState({ key: 'loading', value }));
    };
    setLoadingState(true);

    const requestId = `fetchData-${method}-${url}`;
    if (cancelTokens[requestId] && !input?.withoutCancel) {
      cancelTokens[requestId]?.cancel('Cancelled due to new request');
    }
    const source = axios.CancelToken.source();
    cancelTokens[requestId] = source;

    try {
      let dataPost = JSON.stringify({
        url,
        ...(dataBody.accessToken && { accessToken: dataBody.accessToken }),
        ...(dataBody.login && { login: dataBody.login }),
        ...(dataBody.password && { password: dataBody.password }),
        ...(!input?.withoutMeta && { meta: getMeta() }),
        ...(!input?.withoutMethod && { method }),
      });

      const response = await axios.post(url, dataPost, {
        method: 'post',
        headers: getHeader(true),
        cancelToken: source.token,
      });
      const data = response.data;

      setLoadingState(false);
      return { key, data: data };
    } catch (error: AxiosError | any) {
      setLoadingState(false);

      if (error) {
        const retryResponse = await checkErrorRequest(dispatch, key, error, retryAction, { url, dataBody });
        if (retryResponse?.payload?.data) {
          return { key: retryResponse.payload.key, data: retryResponse.payload.data };
        }
      }
    }
  });
};

export const fetchData = createFetchAsyncThunk('fetch');
export const postData = createFetchAsyncThunk('create');
export const postFile = createFetchAsyncThunk('file');
export const putData = createFetchAsyncThunk('put');
export const patchData = createFetchAsyncThunk('patch');
export const updateData = createFetchAsyncThunk('update');
export const deleteData = createFetchAsyncThunk('delete');
export const decommissionData = createFetchAsyncThunk('decommission');
export const unsetParameterData = createFetchAsyncThunk('unsetParameter');
export const loginMS = loginFetchAsyncThunk('loginMS');
export const logInWithToken = loginFetchAsyncThunk('logInWithToken');
export const logInWithCredentials = loginFetchAsyncThunk('logInWithCredentials');
